'use client';

import Image from '@/components/Image';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebOnly from '@/components/Responsive/WapOnly';
import { useMediaQuery } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTrack } from '../hooks/useTrack';

export interface BannerData {
  type: number; // 1: 图片, 2: 视频
  title?: string;
  title_m?: string;
  description?: string;
  description_m?: string;
  image_pc?: string;
  image_m?: string;
  aspect_ratio_pc?: string;
  aspect_ratio_m?: string;
  word_color?: string;
  word_color_m?: string;
  background_color?: string;
  group?: string | number;
}

export interface BannerProps {
  bannerData: BannerData;
  ts?: number;
  from?: string;
}

export default function Banner({ bannerData, ts = 1, from }: BannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { trackBannerExp } = useTrack();
  const router = useRouter();

  const textPosition =
    from === 'progressive-glasses' || from === 'polarized-glasses'
      ? '!top-[20vw] !bottom-auto'
      : '';

  const textMarginBottom =
    from === 'progressive-glasses' || from === 'polarized-glasses' ? 'mb-8' : '';

  // 获取设备类型
  const isMobileScreen = useMediaQuery('(max-width: 768px)');

  // 使用 useInView 进行曝光检测
  const [bannerRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 视频播放处理
  useEffect(() => {
    const video = videoRef.current;
    if (video && bannerData.type === 2) {
      const playVideo = async () => {
        await video.play();
      };

      playVideo();
    }
  }, [bannerData.type]);

  // 处理曝光埋点
  useEffect(() => {
    if (inView) {
      trackBannerExp(bannerData.group);
    }
  }, [inView, trackBannerExp, bannerData.group]);

  // 获取当前设备对应的数据
  const currentTitle = isMobileScreen ? bannerData.title_m || bannerData.title : bannerData.title;
  const currentDescription = isMobileScreen
    ? bannerData.description_m || bannerData.description
    : bannerData.description;
  const currentImage = isMobileScreen ? bannerData.image_m : bannerData.image_pc;
  const currentAspectRatio = isMobileScreen
    ? bannerData.aspect_ratio_m
    : bannerData.aspect_ratio_pc;
  const currentWordColor = isMobileScreen
    ? bannerData.word_color_m || bannerData.word_color
    : bannerData.word_color;

  // 获取文字框位置和布局样式类名
  const getWordBoxClass = () => {
    // 移动端基础样式：居中布局，限制宽度为90%
    const mobileBase = `flex flex-col items-center justify-center w-[90%] max-w-[90%] text-left ${
      textPosition
    }`;

    // PC端基础样式：左对齐布局，限制宽度为90%
    const pcBase =
      'md:flex md:flex-col md:items-start md:justify-start md:transform md:-translate-y-1/2 md:w-[90%] md:max-w-[90%]';

    return `${mobileBase} bottom-[10vw] md:top-1/2 left-[2.5vw] ${pcBase} md:bottom-6 lg:md:bottom-12`;
  };

  return (
    <div
      ref={bannerRef}
      className="relative flex items-center justify-center md:items-end md:justify-end"
      data-ts={ts}
      style={{
        aspectRatio: currentAspectRatio || (isMobileScreen ? '750/780' : '1920/480'),
        backgroundColor: bannerData.background_color || '#ffffff',
      }}
    >
      {/* 图片或视频内容 */}
      {bannerData.type === 1 ? (
        <Image
          src={currentImage || ''}
          alt={currentTitle || ''}
          fill
          sizes="100vw"
          className="object-cover md:w-auto md:h-full"
        />
      ) : (
        <div className="w-full h-auto">
          <video
            ref={videoRef}
            className="block w-full h-auto"
            loop
            autoPlay
            muted
            preload="auto"
            playsInline
          >
            <source src={currentImage} />
          </video>
        </div>
      )}

      {/* 文字内容 */}
      <div
        className={`absolute box-border ${getWordBoxClass()} md:h-full md:flex md:items-center`}
        style={{ color: currentWordColor }}
      >
        <h1 className={`font-bb text-2xl md:text-[clamp(32px,4vw,56px)] ${textMarginBottom}`}>
          {currentTitle}
        </h1>
        <h5 className={`leading-20 text-[13px] text-center md:text-[clamp(16px,1.5vw,20px)] `}>
          {currentDescription}
        </h5>
      </div>

      {/* 面包屑导航 - 仅移动端显示 */}
      <WebOnly>
        <Breadcrumbs
          items={[
            {
              label: 'Home',
              href: '/',
              onClick: () => {
                router.push('/');
              },
            },
            { label: currentTitle },
          ]}
          className="absolute top-[10px] left-[15px] z-10"
        />
      </WebOnly>
    </div>
  );
}
